import { ServerError } from "modules/common/model/models";
import { useReset } from "modules/common/hooks/useReset";
import { useAuth } from "@clerk/nextjs";
import RequestInit from "next";

export const useFetchWrapper = () => {
  const { getToken, isSignedIn } = useAuth();
  const reset = useReset();

  return {
    get: request("GET"),
    // Public GETs deliberately omit both the Clerk token and browser
    // credentials. This keeps deterministic reads anonymous all the way to
    // the API so signed-in visitors can share the same edge-cache entry.
    publicGet: request("GET", undefined, true),
    post: request("POST"),
    put: request("PUT"),
    delete: request("DELETE"),
    patch: request("PATCH"),
    submit: request("POST", "multipart/form-data"),
  };

  function request(
    method: string,
    cttype?: string,
    publicRead = false
  ): (
    url: string,
    body: any,
    options?: { cache?: RequestInit["cache"] }
  ) => Promise<any> {
    return async (url: string, body: any, options) => {
      const headers = await setHeaders(cttype, publicRead);
      const opts: RequestInit = {
        method,
        headers,
        credentials: publicRead ? "omit" : "include",
        cache: options?.cache,
      };

      if (body) {
        if (cttype) {
          opts.body = body;
        } else {
          opts.body = JSON.stringify(body);
        }
      }

      return fetch(url, opts).then(handleResponse);
    };
  }

  async function setHeaders(
    cttype?: string,
    publicRead = false
  ): Promise<Headers> {
    const headers = new Headers();
    // Only call getToken() when user is confirmed signed in.
    // On middleware-bypassed routes (/city/*, /stores, etc.), Clerk loads
    // from scratch on the client. getToken() blocks until Clerk resolves
    // auth state — for unauthenticated users this can hang indefinitely,
    // blocking all API calls and freezing the page.
    if (!publicRead && isSignedIn) {
      const token = await getToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    // A bodyless public GET needs no Content-Type header. Leaving it out keeps
    // this a CORS-simple request and avoids an unnecessary preflight.
    if (!cttype && !publicRead) {
      headers.set("Content-Type", "application/json");
    }
    return headers;
  }

  function handleResponse(response: any): Promise<any> {
    return response.text().then((text: string) => {
      const data = text && JSON.parse(text);

      if (!response.ok) {
        const err = (data && data.message) || response.statusText;
        const result = {
          code: response.status,
          text: err,
        } as ServerError;
        return Promise.reject(result);
      }

      return Promise.resolve(data);
    });
  }
};
